import { setAuthPresence } from '@/lib/authPresenceWriter'
import { requireSupabaseClient } from '@/lib/supabaseClient'
import { setAuthTokenGetter } from '@/lib/analytics'

let authSessionSnapshot = null

// Let the analytics client stamp signed-in events with the current access token
// so they attribute to auth.uid() server-side (anonymous otherwise).
setAuthTokenGetter(() => authSessionSnapshot?.access_token || null)

function cacheAuthSession(session) {
  authSessionSnapshot = session || null
  // Mirror the session's existence to a shared-domain cookie so the demo host
  // (which can't read studio's localStorage) can tell the user is signed in.
  setAuthPresence(Boolean(authSessionSnapshot))
  return authSessionSnapshot
}

export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase()
}

export function getCachedAuthSession() {
  return authSessionSnapshot
}

export function getSafeNextPath(value, fallback = '/studio') {
  if (!value) return fallback

  let nextPath = String(value)
  try {
    nextPath = decodeURIComponent(nextPath)
  } catch {
    nextPath = String(value)
  }

  if (!nextPath.startsWith('/')) return fallback
  if (nextPath.startsWith('//')) return fallback
  if (nextPath.includes('://')) return fallback

  return nextPath
}

export async function getAuthSession() {
  const supabase = requireSupabaseClient()
  const result = await supabase.auth.getSession()

  if (!result.error) {
    cacheAuthSession(result.data?.session)
  }

  return result
}

export function onAuthStateChange(callback) {
  const supabase = requireSupabaseClient()
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    cacheAuthSession(session)
    callback(event, session)
  })

  return () => {
    data?.subscription?.unsubscribe()
  }
}

export async function requestEmailCode(email) {
  const supabase = requireSupabaseClient()

  return supabase.auth.signInWithOtp({
    email: normalizeEmail(email),
    options: {
      shouldCreateUser: false,
    },
  })
}

// Verifies the 6-digit code the user types in from the sign-in email.
export async function verifyEmailOtp(email, token) {
  const supabase = requireSupabaseClient()

  const result = await supabase.auth.verifyOtp({
    email: normalizeEmail(email),
    token: String(token || '').trim(),
    type: 'email',
  })

  if (!result.error) {
    cacheAuthSession(result.data?.session)
  }

  return result
}

export async function signOut() {
  const supabase = requireSupabaseClient()
  const result = await supabase.auth.signOut()

  if (!result.error) {
    cacheAuthSession(null)
  }

  return result
}
